<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <translator
            v-if="baseDocument"
            :base-document="baseDocument"
            :document-type="documentType"
            :language="form.language"
        ></translator>

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <input-text
            name="name"
            label="* Name"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
            :maxlength=255
        />

        <media-reference
            :language="form.language"
            v-model="form.photo"
            class="mt-3"
            label="Image"
            name="photo_id"
            :media-types="['OperatorLogo']"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Slot Type</p>
            <reference
                create-text="Create new slot type and link slot type to this page"
                link-text="Link an existing slot type"
                :language="form.language"
                v-model="form.slot_type"
                model-data="Slot Type"
                :should-open-new-window=true
                :error="errors.slot_type_id ? errors.slot_type_id[0] : null"
                :creatable="false"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Software</p>
            <reference
                create-text="Create new software and link software to this page"
                link-text="Link an existing software"
                :language="form.language"
                v-model="form.software"
                model-data="Software"
                :should-open-new-window=true
                :creatable="false"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Mobile Slots</p>
            <multi-reference
                create-text="Create new mobile slot and link mobile slot to this page"
                link-text="Link existing mobile slots"
                :language="form.language"
                v-model="form.slot_software_types"
                field-name="slot_software_types"
                model-data="Slot Software Type"
                :should-open-new-window=true
                :creatable="false"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <input-date
            label="Launch Date"
            name="launch_date"
            v-model="form.launch_date"
            class="block max-w-4xl mt-3"
            :error="errors.launch_date ? errors.launch_date[0] : null"
        />

        <input-text
            name="rtp_percentage"
            label="% RTP"
            type="number"
            :step="0.01"
            class="mt-3"
            v-model="form.rtp_percentage"
            :error="errors.rtp_percentage ? errors.rtp_percentage[0] : null"
        />

        <input-text
            name="min_bet"
            label="Min Bet"
            type="number"
            :step="0.01"
            class="mt-3"
            v-model="form.min_bet"
            :error="errors.min_bet ? errors.min_bet[0] : null"
        />

        <input-text
            name="max_bet"
            label="Max Bet"
            type="number"
            :step="0.01"
            class="mt-3"
            v-model="form.max_bet"
            :error="errors.max_bet ? errors.max_bet[0] : null"
        />

        <input-text
            name="reels"
            label="Reels"
            type="number"
            :step="0.01"
            class="mt-3"
            v-model="form.reels"
            :error="errors.reels ? errors.reels[0] : null"
        />

        <input-text
            name="paylines"
            label="Paylines"
            class="mt-3"
            v-model="form.paylines"
            :error="errors.paylines ? errors.paylines[0] : null"
        />

        <input-text
            name="max_coins"
            label="Max Coins"
            type="number"
            :step="0.01"
            class="mt-3"
            v-model="form.max_coins"
            :error="errors.max_coins ? errors.max_coins[0] : null"
        />

        <input-text
            name="theme"
            label="Theme"
            class="mt-3"
            v-model="form.theme"
            :error="errors.theme ? errors.theme[0] : null"
            :maxlength="255"
        />

        <input-text
            name="stakes_range"
            label="Stakes Range"
            class="mt-3"
            v-model="form.stakes_range"
            :error="errors.stakes_range ? errors.stakes_range[0] : null"
            :maxlength="255"
        />

        <input-text
            name="max_win"
            label="Max Win"
            class="mt-3"
            type="number"
            v-model="form.max_win"
            :error="errors.max_win ? errors.max_win[0] : null"
        />

        <input-text
            name="volatility"
            label="Volatility"
            class="mt-3"
            v-model="form.volatility"
            :error="errors.volatility ? errors.volatility[0] : null"
            :maxlength="255"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Company Behind</p>
            <reference
                create-text="Create new company behind and link company behind to this page"
                link-text="Link an existing company behind"
                v-model="form.company_behind"
                model-data="Company Behind"
                :should-open-new-window="true"
                :creatable="false"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <input-text
            name="free_spins"
            label="Free Spins/Multiplier"
            class="mt-3"
            v-model="form.free_spins"
            :error="errors.free_spins ? errors.free_spins[0] : null"
            :maxlength="255"
        />

        <input-checkbox
            name="has_bonus_features"
            label="Has Bonus Features"
            class="mt-3"
            v-model="form.has_bonus_features"
            :error="errors.has_bonus_features ? errors.has_bonus_features[0] : null"
        />

        <input-checkbox
            name="has_bonus_rounds"
            label="Has Bonus Rounds"
            class="mt-3"
            v-model="form.has_bonus_rounds"
            :error="errors.has_bonus_rounds ? errors.has_bonus_rounds[0] : null"
        />

        <input-checkbox
            name="has_freespin"
            label="Has Freespin"
            class="mt-3"
            v-model="form.has_freespin"
            :error="errors.has_freespin ? errors.has_freespin[0] : null"
        />

        <input-checkbox
            name="has_autospin"
            label="Has Autospin"
            class="mt-3"
            v-model="form.has_autospin"
            :error="errors.has_autospin ? errors.has_autospin[0] : null"
        />

        <input-checkbox
            name="has_wild"
            label="Has Wild"
            class="mt-3"
            v-model="form.has_wild"
            :error="errors.has_wild ? errors.has_wild[0] : null"
        />

        <input-checkbox
            name="has_scatters"
            label="Has Scatters"
            class="mt-3"
            v-model="form.has_scatters"
            :error="errors.has_scatters ? errors.has_scatters[0] : null"
        />

        <input-checkbox
            name="has_gamble"
            label="Has Gamble"
            class="mt-3"
            v-model="form.has_gamble"
            :error="errors.has_gamble ? errors.has_gamble[0] : null"
        />

        <input-checkbox
            name="has_multipliers"
            label="Has Multipliers"
            class="mt-3"
            v-model="form.has_multipliers"
            :error="errors.has_multipliers ? errors.has_multipliers[0] : null"
        />

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";

export default {
    mixins: [crudForm],
    name: "SlotForm",
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                name: this.getFormData("name"),
                photo_id: this.getFormData("photo_id"),
                photo: this.getFormData("photo"),
                launch_date: this.getFormData("launch_date"),
                rtp_percentage: this.getFormData("rtp_percentage"),
                min_bet: this.getFormData("min_bet"),
                max_bet: this.getFormData("max_bet"),
                reels: this.getFormData("reels"),
                paylines: this.getFormData("paylines"),
                max_coins: this.getFormData("max_coins"),
                has_bonus_rounds: !!this.getFormData("has_bonus_rounds") || false,
                has_freespin: !!this.getFormData("has_freespin") || false,
                has_autospin: !!this.getFormData("has_autospin") || false,
                has_wild: !!this.getFormData("has_wild") || false,
                has_scatters: !!this.getFormData("has_scatters") || false,
                has_gamble: !!this.getFormData("has_gamble") || false,
                has_multipliers: !!this.getFormData("has_multipliers") || false,
                theme: this.getFormData("theme"),
                stakes_range: this.getFormData("stakes_range"),
                max_win: this.getFormData("max_win"),
                volatility: this.getFormData("volatility"),
                has_bonus_features: !!this.getFormData("has_bonus_features"),
                company_behind_id: this.getFormData("company_behind_id"),
                company_behind: this.getFormData("company_behind"),
                free_spins: this.getFormData("free_spins"),
                slot_type: this.getFormData("slot_type"),
                slot_type_id: this.getFormData("slot_type_id"),
                software: this.getFormData("software"),
                software_id: this.getFormData("software_id"),
                slot_software_types: this.getReferenceData("slot_software_types"),
            },
        };
    },
    watch: {
        "form.photo": function(newValue){
            this.form.photo_id = newValue ? newValue.id : null;
        },
        "form.slot_type": function (newValue) {
            this.form.slot_type_id = newValue ? newValue.id : null;
        },
        "form.software": function (newValue) {
            this.form.software_id = newValue ? newValue.id : null;
        },
        "form.company_behind": function (newValue) {
            this.form.company_behind_id = newValue ? newValue.id : null;
        }
    },
};
</script>
